import { CaretUpFilled, ExportOutlined, FilterFilled } from '@ant-design/icons';
import { Badge, Button, Select, Tooltip } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ModalToolbar from '../../components/ModalToolbar';
import { AttendeeCompanyFilter } from '../utils/attendeeCompany';

type AttendeeListToolbarProps = {
  companyOptions: { label: string; value: AttendeeCompanyFilter }[];
  certificateTypeOptions: { label: string; value: string }[];
  selectedCompanies: AttendeeCompanyFilter[] | null;
  selectedCertificateTypes: string[] | null;
  selectedStatuses: string[] | null;
  statusOptions: { label: string; value: string }[];
  exportDisabled: boolean;
  // Anything the list filters on beyond company, shown next to the export button
  children?: React.ReactNode;
  onCompaniesChange: (companies: AttendeeCompanyFilter[] | null) => void;
  onCertificateTypesChange: (certificateTypes: string[] | null) => void;
  onExport: () => void;
  onStatusesChange: (statuses: string[] | null) => void;
};

const AttendeeListToolbar: React.FC<AttendeeListToolbarProps> = ({
  companyOptions,
  certificateTypeOptions,
  selectedCompanies,
  selectedCertificateTypes,
  selectedStatuses,
  statusOptions,
  children,
  onCompaniesChange,
  onCertificateTypesChange,
  onExport,
  onStatusesChange,
  exportDisabled,
}) => {
  const { t } = useTranslation();
  const [filterVisible, setFilterVisible] = useState(false);
  const hasActiveFilters = Boolean(
    selectedCompanies?.length ||
      selectedCertificateTypes?.length ||
      selectedStatuses?.length,
  );

  const clearFilters = () => {
    onCompaniesChange(null);
    onStatusesChange(null);
    onCertificateTypesChange(null);
  };

  return (
    <ModalToolbar>
      <div className="w-full">
        <div className="flex flex-wrap items-center gap-4">
          <Button
            disabled={exportDisabled}
            icon={<ExportOutlined />}
            onClick={onExport}>
            {t('components.OpenClassAttendeeActions.export')}
          </Button>
          {children}
          <Badge dot={hasActiveFilters}>
            <Tooltip placement="top" title={t('common.filter')}>
              <Button
                aria-label={t('common.filter')}
                icon={filterVisible ? <CaretUpFilled /> : <FilterFilled />}
                shape="circle"
                type="primary"
                onClick={() => setFilterVisible((visible) => !visible)}
              />
            </Tooltip>
          </Badge>
        </div>
        {filterVisible && (
          <div className="mt-4 flex flex-wrap gap-4">
            <Select<AttendeeCompanyFilter[]>
              allowClear
              className="w-72"
              maxTagCount="responsive"
              mode="multiple"
              optionFilterProp="label"
              options={companyOptions}
              placeholder={t(
                'components.OpenClassAttendeeActions.filterCompanies',
              )}
              value={selectedCompanies ?? []}
              onChange={(companyIds) =>
                onCompaniesChange(companyIds.length ? companyIds : null)
              }
            />
            <Select<string[]>
              allowClear
              className="w-72"
              maxTagCount="responsive"
              mode="multiple"
              optionFilterProp="label"
              options={statusOptions}
              placeholder={t(
                'components.OpenClassAttendeeActions.columnStatus',
              )}
              value={selectedStatuses ?? []}
              onChange={(statuses) =>
                onStatusesChange(statuses.length ? statuses : null)
              }
            />
            <Select<string[]>
              allowClear
              className="w-72"
              maxTagCount="responsive"
              mode="multiple"
              optionFilterProp="label"
              options={certificateTypeOptions}
              placeholder={t(
                'components.OpenClassAttendeeActions.columnCertificates',
              )}
              value={selectedCertificateTypes ?? []}
              onChange={(certificateTypes) =>
                onCertificateTypesChange(
                  certificateTypes.length ? certificateTypes : null,
                )
              }
            />
            {hasActiveFilters && (
              <Button onClick={clearFilters}>{t('common.clear')}</Button>
            )}
          </div>
        )}
      </div>
    </ModalToolbar>
  );
};

export default AttendeeListToolbar;
